import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { Button, Flex, Spin, Tabs, TabsProps } from "antd";
import tenderApis from "../../../api/tender/tenderApis";
import dayjs from "dayjs";
import ScoreCardView from "./ScoreCardView";
import TenderComparisonView from "./TenderComparisonView";
import BAFOView from "./BAFOView";
import AIInsightView from "./AIInsightView";
import AskAIView from "./AskAIView";
import Content from "../../../layout/content/Content";
import { RootState } from "../../../store/store";
const BAFOAnalysisView = ({}) => {
  const navigate = useNavigate();
  let { tenderID: tenderId }: any = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [fullDataById, setFullDataById] = React.useState<any>({});
  const [activeTab, setActiveTab] = useState<string>();
  // Remember the most recent non-Ask-AI tab so the fullscreen close button
  // can return the user to where they were instead of always snapping to tab 1.
  const [lastNonAskAiTab, setLastNonAskAiTab] = useState<string>("1");
  const agentAccess = useSelector(
    (state: RootState) => state.agentAccess?.agentAccess
  );
  useEffect(() => {
    if (tenderId) {
      fetchDataById(tenderId);
    }
  }, [tenderId]);
  const fetchDataById = async (id: any) => {
    try {
      setIsLoading(true);
      const response = await tenderApis.getTenderById(Number(id));
      if (response?.status || response?.data) {
        const tenderData = response?.data?.response || response?.data || {};
        setFullDataById(tenderData);
      }
    } catch (error) {
      console.log("Error fetching tender:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const baseItems: TabsProps["items"] = [
    {
      key: "1",
      label: "Score Card",
      children: (
        <ScoreCardView tenderData={fullDataById} activeTab={activeTab} tenderID={tenderId} />
      ),
    },
    {
      key: "2",
      label: "Tender Comparison",
      children: <TenderComparisonView tenderID={tenderId} />,
    },
    {
      key: "3",
      label: "BAFO",
      children: <BAFOView tenderID={tenderId} />,
    },
  ];
  const agentItems: TabsProps["items"] = agentAccess
    ? [
        {
          key: "4",
          label: "AI Insight",
          children: (
            <AIInsightView tenderID={tenderId} tenderData={fullDataById} />
          ),
        },
        {
          key: "5",
          label: "Ask AI",
          // Ask AI escapes its tab container and renders as a fullscreen
          // overlay outside the Tabs — rendering null here keeps the tab
          // selectable without double-mounting the chat surface.
          children: null,
        },
      ]
    : [];
  const items: TabsProps["items"] = [...baseItems, ...agentItems];
  const handleTabChange = (key: string) => {
    if (key !== "5") setLastNonAskAiTab(key);
    setActiveTab(key);
  };

  const siteProject =
    fullDataById?.siteName ||
    fullDataById?.site?.projectName ||
    fullDataById?.site?.siteCode ||
    "--";
  const createdDate =
    fullDataById?.createdAt || fullDataById?.createdDate
      ? dayjs(fullDataById.createdAt || fullDataById.createdDate).format(
          "DD-MMM-YYYY hh:mm A"
        )
      : "--";

  const tabExtra = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
        fontSize: 13,
      }}
    >
      <span>
        <span style={{ color: "#8c8c8c" }}>Tender # </span>
        <strong style={{ color: "#1a1a1a" }}>
          {fullDataById?.tenderNumber || fullDataById?.tenderID || "--"}
        </strong>
      </span>
      <span>
        <span style={{ color: "#8c8c8c" }}>Site / Project </span>
        <strong style={{ color: "#1a1a1a" }}>{siteProject}</strong>
      </span>
      <span>
        <span style={{ color: "#8c8c8c" }}>Created </span>
        <strong style={{ color: "#1a1a1a" }}>{createdDate}</strong>
      </span>
      <Button type="default" size="small" onClick={() => navigate(-1)}>
        <em
          className="icon ni ni-arrow-left"
          style={{ marginRight: 5 }}
        ></em>
        Back
      </Button>
    </div>
  );

  const isAskAiActive = activeTab === "5" && agentAccess;

  return (
    <>
      <Content>
        {isLoading == true ? (
          <>
            <Flex align="center" gap="middle" className="pageLoader">
              <Spin size="large" className="m-auto" />
            </Flex>
          </>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={items}
            tabBarExtraContent={tabExtra}
            tabBarStyle={{
              color: "#2263b3",
              fontSize: "20px",
            }}
            tabBarGutter={32}
            renderTabBar={(props, DefaultTabBar) => (
              <DefaultTabBar {...props} style={{ marginBottom: "0" }} />
            )}
          />
        )}
      </Content>
      {isAskAiActive && (
        <AskAIView
          tenderID={tenderId}
          tenderData={fullDataById}
          fullScreen
          onExit={() => handleTabChange(lastNonAskAiTab)}
        />
      )}
    </>
  );
};

export default BAFOAnalysisView;
